/**
 * AskPanel — the "Ask" mode of the command palette.
 *
 * Phase 1: a KB-grounded Q&A box. The operator types a question (e.g. "what's the cabling standard
 * for an EAP104?"), hits Enter/Ask, and gets an answer summarised from the knowledge base with
 * citations back to the source standards. Read-only: it retrieves and summarises, it never changes
 * anything (confirm-gated actions are a later phase).
 *
 * FAIL CLOSED (AI governance P0-A): when no model backend answers, the server returns
 * available=false and a fixed "unavailable" sentence — never KB or prompt text — and this panel says
 * so plainly. A real answer is labelled "AI-generated · <model>"; "From the knowledge base" is shown
 * only when the server marks it grounded (the model answered AND cited a retrieved snippet).
 *
 * This owns its OWN input + state and submits EXPLICITLY (Enter / Ask button) — deliberately NOT a
 * debounced live query like search, because a natural-language answer is not cheap. CommandPalette
 * keeps this mounted while the "search" tab is active, so an in-flight answer survives a tab flip.
 */
import { useState, type KeyboardEvent } from "react";

import { ApiError } from "@/lib/api/client";
import { useAskAssistant } from "@/lib/api/hooks";

export function AskPanel() {
  const [question, setQuestion] = useState("");
  const ask = useAskAssistant();
  const result = ask.data;

  function submit() {
    const q = question.trim();
    if (q.length < 3 || ask.isPending) return;
    ask.mutate(q);
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    // Enter submits; Shift+Enter is a newline. Esc is left to the palette shell to close.
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  }

  return (
    <div className="flex flex-col gap-3 px-4 py-4">
      <div className="flex items-end gap-2">
        <textarea
          autoFocus
          rows={2}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Ask about standards, runbooks, onboarding… (Enter to send)"
          className="min-h-[2.5rem] flex-1 resize-none rounded-md border border-line bg-surface-2
                     px-3 py-2 text-sm text-ink placeholder:text-ink-subtle
                     focus:outline-none focus:ring-2 focus:ring-brand"
        />
        <button
          type="button"
          onClick={submit}
          disabled={question.trim().length < 3 || ask.isPending}
          className="rounded-md bg-brand px-3 py-2 text-sm font-medium text-white
                     disabled:opacity-50"
        >
          {ask.isPending ? "Asking…" : "Ask"}
        </button>
      </div>

      {ask.isError && (
        <p className="text-sm text-critical" data-testid="ask-error">
          {/* A 403 is the server's plain-language "MSP operators only" text (api/v2/assistant.py
              _KB_DENIED); show it verbatim. Anything else stays the generic retry hint. */}
          {ask.error instanceof ApiError && ask.error.status === 403
            ? ask.error.message
            : "Couldn't reach the assistant. Try again."}
        </p>
      )}

      {result && !ask.isPending && (
        <div className="flex flex-col gap-3">
          {result.available === false ? (
            <span className="inline-flex w-fit items-center rounded-full bg-surface-2 px-2 py-0.5
                             text-xs text-ink-muted" data-testid="ask-unavailable">
              AI assistant unavailable
            </span>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              {result.model && (
                <span className="inline-flex w-fit items-center rounded-full bg-surface-2 px-2 py-0.5
                                 text-xs text-ink-muted">
                  AI-generated · {result.model}
                </span>
              )}
              {!result.grounded && (
                <span className="inline-flex w-fit items-center rounded-full bg-surface-2 px-2 py-0.5
                                 text-xs text-ink-muted">
                  Not from the knowledge base
                </span>
              )}
            </div>
          )}
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-ink">{result.answer}</p>

          {result.citations.length > 0 && (
            <div className="flex flex-col gap-1 border-t border-line pt-2">
              <p className="text-xs font-medium uppercase tracking-wide text-ink-subtle">Sources</p>
              {result.citations.map((c) => (
                <div key={c.n} className="text-xs text-ink-muted">
                  <span className="text-ink-subtle">[{c.n}]</span>{" "}
                  {c.url ? (
                    <a href={c.url} target="_blank" rel="noreferrer"
                       className="text-brand hover:underline">
                      {c.title}
                    </a>
                  ) : (
                    <span className="text-ink">{c.title}</span>
                  )}
                  {c.heading && <span className="text-ink-subtle"> · {c.heading}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!result && !ask.isPending && !ask.isError && (
        <p className="text-xs text-ink-subtle">
          Answers are AI-generated from your standards and runbooks, with sources cited. Check the
          sources before acting. Read-only.
        </p>
      )}
    </div>
  );
}
